/**
 * LayerContext — 화면을 덮는 레이어(다이얼로그·바텀시트·로딩 오버레이) 등록부
 *
 * 왜 필요한가
 *   토스트가 모달 위에 겹쳐 뜨면 사용자는 둘 중 하나를 놓친다. 레이어가 하나라도 열려 있으면
 *   Toast.js가 오류가 아닌 토스트를 잠시 보관했다가, 마지막 레이어가 닫힌 뒤 보여준다.
 *
 * 사용법
 *   App.js:      <LayerProvider>…</LayerProvider>  (ToastProvider 바깥)
 *   Dialog/Sheet: useRegisterLayer(open)            — 열려 있는 동안 자동 등록·해제
 *   소비자:       const { hasLayer, layerCount, topLayerId } = useLayer();
 *
 * Provider 밖에서 호출하면 (격리 렌더·테스트) 아무 것도 하지 않는 안전한 기본값을 돌려준다.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';

const LayerContext = createContext(null);

const noop = () => {};

const FALLBACK_LAYER_STATE = Object.freeze({
  hasLayer: false,
  layerCount: 0,
  layers: [],
  topLayerId: null,
  register: noop,
  unregister: noop,
  hasProvider: false,
});

export const LayerProvider = ({ children }) => {
  const [layers, setLayers] = useState([]);

  const register = useCallback((id) => {
    setLayers((previous) => (previous.includes(id) ? previous : [...previous, id]));
  }, []);

  const unregister = useCallback((id) => {
    setLayers((previous) => (
      previous.includes(id) ? previous.filter((layerId) => layerId !== id) : previous
    ));
  }, []);

  const value = useMemo(() => ({
    hasLayer: layers.length > 0,
    layerCount: layers.length,
    layers,
    topLayerId: layers.length > 0 ? layers[layers.length - 1] : null,
    register,
    unregister,
    hasProvider: true,
  }), [layers, register, unregister]);

  return (
    <LayerContext.Provider value={value}>
      {children}
    </LayerContext.Provider>
  );
};

/**
 * 현재 레이어 상태를 읽는다. Provider 밖에서는 항상 "레이어 없음"으로 응답한다.
 */
export const useLayer = () => useContext(LayerContext) || FALLBACK_LAYER_STATE;

/**
 * active가 true인 동안 이 컴포넌트를 레이어로 등록한다.
 * 언마운트·active=false 전환 시 자동 해제되어 중첩·겹침 모달에서도 카운트가 어긋나지 않는다.
 *
 * @param {boolean} active
 * @param {string}  [explicitId]  디버깅용 식별자. 생략하면 useId()로 만든다.
 * @returns {string} 등록에 사용한 id
 */
export const useRegisterLayer = (active, explicitId) => {
  const generatedId = useId();
  const id = explicitId || generatedId;
  const { register, unregister } = useLayer();

  useEffect(() => {
    if (!active) return undefined;
    register(id);
    return () => unregister(id);
  }, [active, id, register, unregister]);

  return id;
};

export default LayerContext;
